import { AccountViewListingGroupType } from '@zg-rentals/ts-rental-manager-api-client';

import PropertyConstants from '../constants/PropertyConstants';

import { getPhotoUrls } from './propertiesPageUtils';
import propertyUtils from './propertyUtils';
import stringHelper from './stringHelper';

import type { AccountViewListingGroupDto, AccountViewUnitCountsDto } from '@zg-rentals/ts-rental-manager-api-client';

const { PROPERTY_TYPES, PROPERTY_TYPES_BY_ENUM } = PropertyConstants;

const labelForPropertyTypesKey = (key: string | undefined) => {
  const k = key?.trim();
  if (k == null || k === '' || !Object.hasOwn(PROPERTY_TYPES, k)) {
    return undefined;
  }
  return PROPERTY_TYPES[k as keyof typeof PROPERTY_TYPES];
};

// `propertyType` values are PropertyType enums (e.g. LARGE, DIVIDED, GARDEN) that map to a display label.
const labelForPropertyTypeEnum = (propertyType: string | undefined) => {
  const key = propertyType?.trim();
  if (key == null || key === '' || !Object.hasOwn(PROPERTY_TYPES_BY_ENUM, key)) {
    return undefined;
  }
  return PROPERTY_TYPES_BY_ENUM[key];
};

export const getPropertyTypeLabel = (listing: AccountViewListingGroupDto) => {
  if (listing.type === AccountViewListingGroupType.MULTIPLEX) {
    return labelForPropertyTypesKey(listing.type);
  }
  if (listing.type === AccountViewListingGroupType.LISTING) {
    return labelForPropertyTypeEnum(listing.propertyType);
  }
  return undefined;
};

export const getAddressLines = (listing: AccountViewListingGroupDto) => {
  const { address, name } = listing;
  const streetLine = address.unit ? `${address.street} #${address.unit}` : address.street || name;
  const { formattedCityStateZip } = propertyUtils.formatAddress(address as unknown as Record<string, string>);

  return { streetLine, cityStateZip: formattedCityStateZip };
};

export const getFormattedAddress = (listing: AccountViewListingGroupDto) => {
  const { streetLine, cityStateZip } = getAddressLines(listing);

  return `${streetLine} ${cityStateZip}`.trim();
};

export const getUnitSummary = (listing: AccountViewListingGroupDto & { unitCount?: AccountViewUnitCountsDto }) => {
  const { total, vacant } = listing.unitCount ?? {};
  const unitLabel = total == null ? '— units' : stringHelper.pluralize(total, 'unit', 'units');
  const vacantLabel = stringHelper.pluralize(vacant ?? 0, 'vacancy', 'vacancies');

  return `${unitLabel} • ${vacantLabel}`;
};

export const getPhotoThumbUrl = (photoKey: string | undefined) => {
  if (!photoKey) {
    return '';
  }

  const isRunningInProd =
    typeof window !== 'undefined'
      ? window.location.hostname.includes('zillow.com') || window.location.hostname.includes('hotpads.com')
      : false;

  return getPhotoUrls(isRunningInProd, photoKey).thumb;
};
